import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ToastService } from '../../_services/toast.service';
import { GatewayTableComponent } from '../gateway-table/gateway-table.component';
import { DeviceTableComponent } from '../device-table/device-table.component';
import { DataService } from '../../_services/data.service';
import { RegionUtils } from '../../utilities/region.utils';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-tenant-detail',
  templateUrl: './tenant-detail.component.html',
  styleUrls: ['./tenant-detail.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    GatewayTableComponent,
    DeviceTableComponent,
    FormsModule,
  ],
})
export class TenantDetailComponent implements OnInit {
  tenant: any;
  isApiKeyVisible = false;
  isEmailPasswordVisible = false;
  gatewayCount = 0;
  deviceCount = 0;
  gateways: any[] = [];
  devices: any[] = [];
  smtpIntegrations: any[] = [];
  lnsIntegrations: any[] = [];
  selectedEmailIntegration: string = '';
  selectedLnsIntegration: string = '';
  isEditingName = false;
  editingName: string = '';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private dataService: DataService,
    private toast: ToastService,
    private httpClient: HttpClient
  ) {}

  ngOnInit(): void {
    const tenantId = this.route.snapshot.paramMap.get('id');
    if (tenantId) {
      this.loadTenantDetails(tenantId);
      this.loadIntegrations();
    }
  }

  loadTenantDetails(id: string): void {
    this.dataService.getTenantById(id).subscribe(
      data => {
        this.tenant = data;
        this.selectedEmailIntegration = (data as any).emailIntegration || '';
        this.selectedLnsIntegration = (data as any).lnsIntegration || '';
        this.loadGatewaysAndDevices();
      },
      error => {
        this.toast.show('Error', 'Error loading tenant details', 'danger');
        console.error('Error loading tenant details:', error);
      }
    );
  }

  loadGatewaysAndDevices(): void {
    // Load gateways
    this.dataService.getGateways().subscribe(
      (gateways: any[]) => {
        this.gateways = gateways.filter(g => g.tenant?._id === this.tenant._id);
      },
      error => {
        console.error('Error loading gateways:', error);
      }
    );

    // Load devices
    this.dataService.getDevices().subscribe(
      (devices: any[]) => {
        this.devices = devices.filter(d => d.tenant === this.tenant._id);
      },
      error => {
        console.error('Error loading devices:', error);
      }
    );
  }

  onGatewayCountChange(count: number): void {
    this.gatewayCount = count;
  }

  onDeviceCountChange(count: number): void {
    this.deviceCount = count;
  }

  maskSensitiveData(value: string): string {
    if (!value) return '';
    if (value.length <= 6) return value;
    return (
      value.substring(0, 3) +
      '•'.repeat(value.length - 6) +
      value.substring(value.length - 3)
    );
  }

  copyToClipboard(text: string): void {
    navigator.clipboard.writeText(text).then(
      () => {
        this.toast.show('Success', 'Copied to clipboard', 'success');
      },
      () => {
        this.toast.show('Error', 'Failed to copy', 'danger');
      }
    );
  }

  getRegionFlag(region: string): string {
    return RegionUtils.getRegionFlag(region);
  }

  loadIntegrations(): void {
    this.dataService.getIntegrations('SMTP').subscribe(
      (integrations: any[]) => {
        this.smtpIntegrations = integrations.filter(i => i.isActive);
      },
      error => {
        console.error('Error loading SMTP integrations:', error);
      }
    );

    this.dataService.getIntegrations('LNS').subscribe(
      (integrations: any[]) => {
        this.lnsIntegrations = integrations.filter(i => i.isActive);
      },
      error => {
        console.error('Error loading LoRaWAN Network Servers:', error);
      }
    );
  }

  onEmailIntegrationChange(): void {
    this.updateIntegration(
      'emailIntegration',
      this.selectedEmailIntegration,
      'Email integration'
    );
  }

  onLnsIntegrationChange(): void {
    this.updateIntegration(
      'lnsIntegration',
      this.selectedLnsIntegration,
      'LoRaWAN Network Server'
    );
  }

  private updateIntegration(
    field: string,
    value: string,
    displayName: string
  ): void {
    // Create clean payload with only the integration field to update
    const integrationUpdate = {
      [field]: value || null,
    };

    // Construct URL directly without including tenantId in request body
    const url = `${environment.apiPrefix}${
      (this.dataService as any).currentTenantId
    }${environment.apiSuffix}/tenants/${this.tenant._id}`;

    // Make direct PATCH request with only the integration field
    this.httpClient.patch(url, integrationUpdate).subscribe(
      (response: any) => {
        (this.tenant as any)[field] = value;
        this.toast.show(
          'Success',
          `${displayName} updated successfully`,
          'success'
        );
      },
      (error: any) => {
        this.toast.show(
          'Error',
          `Error updating ${displayName.toLowerCase()}`,
          'danger'
        );
        console.error(`Error updating ${displayName.toLowerCase()}:`, error);
        if (field === 'emailIntegration') {
          this.selectedEmailIntegration = this.tenant.emailIntegration || '';
        } else if (field === 'lnsIntegration') {
          this.selectedLnsIntegration =
            (this.tenant as any).lnsIntegration || '';
        }
      }
    );
  }

  startNameEdit(): void {
    this.isEditingName = true;
    this.editingName = this.tenant?.name || '';
    // Focus the input after view update
    setTimeout(() => {
      const nameInput = document.querySelector(
        'input[type="text"]'
      ) as HTMLInputElement;
      if (nameInput) {
        nameInput.focus();
        nameInput.select();
      }
    }, 0);
  }

  saveName(): void {
    if (!this.editingName.trim()) {
      this.toast.show('Error', 'Tenant name cannot be empty', 'danger');
      return;
    }

    if (this.editingName === this.tenant?.name) {
      this.cancelNameEdit();
      return;
    }

    this.updateTenantField('name', this.editingName.trim(), 'Tenant name');
  }

  cancelNameEdit(): void {
    this.isEditingName = false;
    this.editingName = '';
  }

  private updateTenantField(
    field: string,
    value: string,
    displayName: string
  ): void {
    const updateData = {
      [field]: value,
    };

    const url = `${environment.apiPrefix}${
      (this.dataService as any).currentTenantId
    }${environment.apiSuffix}/tenants/${this.tenant._id}`;

    this.httpClient.patch(url, updateData).subscribe(
      (response: any) => {
        (this.tenant as any)[field] = value;
        this.isEditingName = false;
        this.editingName = '';
        this.toast.show(
          'Success',
          `${displayName} updated successfully`,
          'success'
        );
      },
      (error: any) => {
        this.toast.show(
          'Error',
          `Error updating ${displayName.toLowerCase()}`,
          'danger'
        );
        console.error(`Error updating ${displayName.toLowerCase()}:`, error);
        // Don't reset editing state on error so user can try again
      }
    );
  }

  onToggle2FA(event: any): void {
    const enable2FA = event.target.checked;

    const configUpdate = {
      config: {
        enable2FA: enable2FA,
      },
    };

    const url = `${environment.apiPrefix}${
      (this.dataService as any).currentTenantId
    }${environment.apiSuffix}/tenants/${this.tenant._id}`;

    this.httpClient.patch(url, configUpdate).subscribe(
      (response: any) => {
        if (!this.tenant.config) {
          this.tenant.config = {};
        }
        this.tenant.config.enable2FA = enable2FA;
        this.toast.show(
          'Success',
          `Two-Factor Authentication ${
            enable2FA ? 'enabled' : 'disabled'
          } successfully`,
          'success'
        );
      },
      (error: any) => {
        this.toast.show(
          'Error',
          'Error updating Two-Factor Authentication setting',
          'danger'
        );
        console.error('Error updating 2FA setting:', error);
        event.target.checked = !enable2FA;
      }
    );
  }

  onToggleLorawanServerSync(event: any): void {
    const lorawanServerSync = event.target.checked;

    const configUpdate = {
      config: {
        lorawanServerSync: lorawanServerSync,
      },
    };

    const url = `${environment.apiPrefix}${
      (this.dataService as any).currentTenantId
    }${environment.apiSuffix}/tenants/${this.tenant._id}`;

    this.httpClient.patch(url, configUpdate).subscribe(
      (response: any) => {
        if (!this.tenant.config) {
          this.tenant.config = {};
        }
        this.tenant.config.lorawanServerSync = lorawanServerSync;
        this.toast.show(
          'Success',
          `LoRaWAN Network Server Sync ${
            lorawanServerSync ? 'enabled' : 'disabled'
          } successfully`,
          'success'
        );
      },
      (error: any) => {
        this.toast.show(
          'Error',
          'Error updating LoRaWAN Network Server Sync setting',
          'danger'
        );
        console.error(
          'Error updating LoRaWAN Network Server Sync setting:',
          error
        );
        event.target.checked = !lorawanServerSync;
      }
    );
  }

  onTokenExpiryChange(event: any): void {
    const tokenExpiry = event.target.value || null;

    const configUpdate = {
      config: {
        tokenExpiry: tokenExpiry,
      },
    };

    const url = `${environment.apiPrefix}${
      (this.dataService as any).currentTenantId
    }${environment.apiSuffix}/tenants/${this.tenant._id}`;

    this.httpClient.patch(url, configUpdate).subscribe(
      (response: any) => {
        if (!this.tenant.config) {
          this.tenant.config = {};
        }
        this.tenant.config.tokenExpiry = tokenExpiry;
        const displayValue = tokenExpiry || 'Never';
        this.toast.show(
          'Success',
          `User Login Session Expiry updated to ${displayValue} successfully`,
          'success'
        );
      },
      (error: any) => {
        this.toast.show(
          'Error',
          'Error updating User Login Session Expiry setting',
          'danger'
        );
        console.error('Error updating token expiry:', error);
      }
    );
  }

  goBack(): void {
    this.router.navigate(['/apps']);
  }
}
